import axios from '@nextcloud/axios';
import { showError } from '@nextcloud/dialogs';

import { getAlbumFileInfos } from './albums';
import client, { remotePath } from './client';

import { API } from '@services/API';
import { translate as t } from '@services/l10n';
import * as utils from '@services/utils';
import * as nativex from '@native';

import type { IFileInfo, IImageInfo, IPhoto } from '@typings';
import type { ResponseDataDetailed, SearchResult } from 'webdav';

const GET_FILE_CHUNK_SIZE = 50;

type GetFilesOpts = {
  /** Attempt to use some cached value to get filename (default true) */
  cache?: boolean;
  /** Get original route-independent filename for current user only (default false) */
  ignoreRoute?: boolean;
};

/**
 * Get file infos for list of files given Ids
 * @param photos list of photos
 * @param opts options
 * @details This tries to use the cached filename in the photo object (imageInfo.filename)
 * If none was found, then it will fetch the file info from the server.
 */
export async function getFiles(photos: IPhoto[], opts?: GetFilesOpts): Promise<IFileInfo[]> {
  // Some routes may have special handling of filenames
  if (!opts?.ignoreRoute) {
    if (_m.route.name === _m.routes.Albums.name) {
      return getAlbumFileInfos(photos, _m.route.params.user?.toString(), _m.route.params.name?.toString());
    }
  }

  // Remove any local photos
  photos = photos.filter((photo) => !utils.isLocalPhoto(photo));

  // Cache and uncached photos
  const cache: IFileInfo[] = [];
  const rest: IPhoto[] = [];

  // Partition photos with and without cache
  if (opts?.cache !== false) {
    for (const photo of photos) {
      const filename = photo.imageInfo?.filename;
      if (filename) {
        // For public shares, use files/{token} path (WebDAV path for public shares)
        // For logged in users, use files/{uid} path
        let originalFilename: string;
        if (utils.uid) {
          originalFilename = `/files/${utils.uid}${filename}`;
        } else {
          // Public share: use token-based WebDAV path
          const token = _m.route.params.token?.toString();
          originalFilename = `/files/${token}${filename}`;
        }

        cache.push({
          id: photo.fileid,
          fileid: photo.fileid,
          basename: photo.basename ?? filename.split('/').pop() ?? '',
          originalFilename: originalFilename,
          filename: filename,
        });
      } else {
        rest.push(photo);
      }
    }
  }

  // Get file infos for the rest
  return [...cache, ...(await getFilesInternal1(rest))];
}

async function getFilesInternal1(photos: IPhoto[]): Promise<IFileInfo[]> {
  // For public shares, use API instead of WebDAV SEARCH (which requires user auth)
  if (!utils.uid) {
    return getFilesViaAPI(photos);
  }

  // Get file IDs array
  const fileIds = photos.map((photo) => photo.fileid);

  // Divide fileIds into chunks of GET_FILE_CHUNK_SIZE
  const chunks: number[][] = [];
  for (let i = 0; i < fileIds.length; i += GET_FILE_CHUNK_SIZE) {
    chunks.push(fileIds.slice(i, i + GET_FILE_CHUNK_SIZE));
  }

  // Get file infos for each chunk
  return (await Promise.all(chunks.map(getFilesInternal2))).flat();
}

/**
 * Get file infos for public shares via API (instead of WebDAV SEARCH)
 * This is needed because WebDAV SEARCH requires user authentication
 */
async function getFilesViaAPI(photos: IPhoto[]): Promise<IFileInfo[]> {
  const fileInfos: IFileInfo[] = [];
  const token = _m.route.params.token?.toString();

  for (const photo of photos) {
    try {
      const url = API.IMAGE_INFO(photo.fileid);
      const res = await axios.get<IImageInfo>(url);
      const filename = res.data.filename;

      if (filename) {
        // Use token-based WebDAV path for public shares
        fileInfos.push({
          id: photo.fileid,
          fileid: photo.fileid,
          basename: photo.basename ?? filename.split('/').pop() ?? '',
          originalFilename: `/files/${token}${filename}`,
          filename: filename,
        });
      }
    } catch (error) {
      console.error('Failed to get file info via API', photo.fileid, error);
    }
  }

  return fileInfos;
}

async function getFilesInternal2(fileIds: number[]): Promise<IFileInfo[]> {
  // This function is only called for authenticated users (not public shares)
  // Public shares use getFilesViaAPI instead to avoid WebDAV SEARCH auth issues
  const prefixPath = `/files/${utils.uid}`;

  // IMPORTANT: if this isn't there, then a blank
  // returns EVERYTHING on the server!
  if (fileIds.length === 0) {
    return [];
  }

  // https://jsonformatter.org/xml-formatter
  const filter = fileIds
    .map((fileId) => `<d:eq><d:prop><oc:fileid/></d:prop><d:literal>${fileId}</d:literal></d:eq>`)
    .join('');

  // Make Search request
  const xml = `<?xml version="1.0" encoding="UTF-8"?><d:searchrequest xmlns:d="DAV:" xmlns:oc="http://owncloud.org/ns" xmlns:nc="http://nextcloud.org/ns" xmlns:ns="https://github.com/icewind1991/SearchDAV/ns" xmlns:ocs="http://open-collaboration-services.org/ns"><d:basicsearch><d:select><d:prop><oc:fileid /></d:prop></d:select><d:from><d:scope><d:href>${prefixPath}</d:href><d:depth>0</d:depth></d:scope></d:from><d:where><d:or>${filter}</d:or></d:where></d:basicsearch></d:searchrequest>`;
  const response = (await client.search('', { data: xml, details: true })) as ResponseDataDetailed<SearchResult>;

  return response.data.results
    .filter((file) => file.props?.fileid)
    .map((file) => {
      // remote remotePath from start
      if (file.filename.startsWith(remotePath)) {
        file.filename = file.filename.slice(remotePath.length);
      }

      // create IFileInfo
      return {
        id: file.props!.fileid as number,
        fileid: file.props!.fileid as number,
        basename: file.basename,
        originalFilename: file.filename,
        filename: file.filename.replace(prefixPath, ''),
      };
    });
}

/**
 * Run promises in parallel, but only n at a time
 * @param promises Array of promise generator funnction (async functions)
 * @param n Number of promises to run in parallel
 * @details Each promise returned MUST resolve and not throw an error
 * @returns Generator of lists of results. Each list is of length n.
 */
export async function* runInParallel<T>(promises: (() => Promise<T>)[], n: number) {
  if (!promises.length) return;

  promises.reverse(); // reverse so we can use pop() efficiently

  const results: T[] = [];
  const running: Promise<void>[] = [];

  while (true) {
    // add one promise per iteration
    if (promises.length) {
      let task!: Promise<void>;
      running.push(
        (task = (async () => {
          // run the promise
          results.push(await promises.pop()!());

          // remove the promise from the running list
          running.splice(running.indexOf(task), 1);
        })()),
      );
    }

    // wait for one of the promises to finish
    if (running.length >= n || !promises.length) {
      await Promise.race(running);
    }

    // yield the results if the threshold is reached
    if (results.length >= n || !running.length) {
      yield results.splice(0, results.length);
    }

    // stop when all promises are done
    if (!running.length && !promises.length) break;
  }
}

type ExtendedStack = {
  photos: IPhoto[];
  livePhotoVideoFileIds: Set<number>;
};

/**
 * Extend given list of Ids with extra files for
 *
 * 1. Live Photos.
 * 2. Stacked RAW files.
 *
 * @param photos list of photos to search for
 *
 * @returns expanded photos and the IDs of Live Photo video parts
 */
export async function extendWithStack(photos: IPhoto[]): Promise<ExtendedStack> {
  // Add Live Photos files
  const livePhotos: IPhoto[] = [];
  for await (const res of runInParallel(
    photos
      .filter((p) => p.liveid && !p.liveid.startsWith('self__'))
      .map((p) => async () => {
        try {
          const base = utils.getLivePhotoVideoUrl(p, false);
          const url = API.Q(base, { format: 'json' });
          const res = await axios.get<IPhoto>(url);
          return res.data;
        } catch (error) {
          console.error(error);
          return null;
        }
      }),
    10,
  )) {
    livePhotos.push(...res.filter(utils.truthy));
  }

  // Add stacked RAW files (deduped)
  const stackRaw = photos.map((p) => p.stackraw ?? []).flat();

  // Combine all files
  const combined = [...photos, ...livePhotos, ...stackRaw];

  // De-duplicate keeping the order same as before
  // https://github.com/pulsejet/memories/issues/1056
  const cIds = new Set(combined.map((p) => p.fileid));
  return {
    photos: combined.filter((p) => cIds.delete(p.fileid)),
    livePhotoVideoFileIds: new Set(livePhotos.map((p) => p.fileid)),
  };
}

/**
 * Delete all files in a given list of Ids
 *
 * @param photos list of photos to delete
 * @param confirm whether to show a confirmation dialog (default true)
 * @returns list of file ids that were deleted
 */
export async function* deletePhotos(photos: IPhoto[], confirm: boolean = true) {
  if (photos.length === 0) return;

  // Use user selected count for confirmation
  const confirmationCount = photos.length;

  // Extend with stack unless this is an album
  const routeIsAlbums = _m.route.name === _m.routes.Albums.name;
  let livePhotoVideoFileIds = new Set<number>();
  if (!routeIsAlbums) {
    const extended = await extendWithStack(photos);
    photos = extended.photos;
    livePhotoVideoFileIds = extended.livePhotoVideoFileIds;
  }

  // Get set of unique file ids
  const fileIdsSet = new Set(photos.map((p) => p.fileid));

  // Get files data. The double filter ensures we never delete something accidentally.
  const fileInfos = (await getFiles(photos)).filter((f) => fileIdsSet.has(f.fileid));
  const regularFileInfos = fileInfos.filter((f) => !livePhotoVideoFileIds.has(f.fileid));
  const livePhotoFileInfos = fileInfos.filter((f) => livePhotoVideoFileIds.has(f.fileid));

  // Check for locally available files and delete them.
  // For albums, we are not actually deleting.
  const hasNative = nativex.has() && !routeIsAlbums;

  // Check if native confirmation is available
  if (hasNative) {
    confirm &&= (await nativex.deleteLocalPhotos(photos, true)) !== photos.length;
  }

  // Show confirmation dialog if required
  if (confirm) {
    if (routeIsAlbums) {
      if (!(await utils.dialogs.removeFromAlbum(confirmationCount))) {
        throw new Error('User cancelled removal');
      }
    } else {
      if (!(await utils.dialogs.moveToTrash(confirmationCount))) {
        throw new Error('User cancelled deletion');
      }
    }
  }

  // Delete local files.
  if (hasNative) {
    // Delete local files.
    await nativex.deleteLocalPhotos(photos);

    // Remove purely local files
    const deleted = photos.filter(utils.isLocalPhoto);

    // Yield for the fully local files
    if (deleted.length > 0) {
      yield deleted.map((f) => f.fileid);
    }
  }

  /** Delete a server file. */
  const _delete_file = async (fileInfo: IFileInfo, silenceErrors: boolean = false) => {
    try {
      if (!utils.uid) {
        // Public share: use API endpoint which supports token authentication
        await axios.delete(API.IMAGE_DELETE(fileInfo.fileid));
      } else {
        // Authenticated user: use WebDAV
        await client.deleteFile(fileInfo.originalFilename);
      }
      return fileInfo.fileid;
    } catch (error) {
      if (silenceErrors) return 0;
      console.error('Failed to delete', fileInfo, error);
      showError(
        t('memories', 'Failed to delete {fileName}.', {
          fileName: fileInfo.filename,
        }),
      );
      return 0;
    }
  };

  const calls = regularFileInfos.map((fileInfo) => async () => _delete_file(fileInfo));
  yield* runInParallel(calls, 10);
  // Try to delete the live photo video parts but silently ignore errors
  const livePhotoVideoCalls = livePhotoFileInfos.map((fileInfo) => () => _delete_file(fileInfo, true));
  for await (const _ of runInParallel(livePhotoVideoCalls, 10)) {
    // ignore results for live photo video operations
  }
}

/**
 * copy all files in a given list of Ids to given destination
 *
 * @param photos list of photos to copy
 * @param destination to copy photos into
 * @param overwrite behaviour if the target exists. `true` overwrites, `false` fails.
 * @returns list of file ids that were copied
 */
export async function* copyPhotos(photos: IPhoto[], destination: string, overwrite: boolean) {
  if (photos.length === 0) {
    return;
  }

  // Set absolute target path
  const prefixPath = `files/${utils.uid}`;
  let targetPath = prefixPath + destination;
  if (!targetPath.endsWith('/')) {
    targetPath += '/';
  }

  // Also copy the stack files
  const extended = await extendWithStack(photos);
  photos = extended.photos;
  const livePhotoVideoFileIds = extended.livePhotoVideoFileIds;
  const fileIdsSet = new Set(photos.map((p) => p.fileid));

  // Get files data
  let fileInfos: IFileInfo[] = [];
  try {
    fileInfos = await getFiles(photos);
  } catch (e) {
    console.error('Failed to get file info for files to copy', photos, e);
    showError(t('memories', 'Failed to copy files.'));
    return;
  }

  const regularFileInfos = fileInfos.filter((f) => fileIdsSet.has(f.fileid) && !livePhotoVideoFileIds.has(f.fileid));
  const livePhotoVideoFileInfos = fileInfos.filter((f) => livePhotoVideoFileIds.has(f.fileid));

  /** Copy a server file */
  const _copy_file = async (fileInfo: IFileInfo, silenceErrors: boolean = false) => {
    try {
      await client.copyFile(
        fileInfo.originalFilename,
        targetPath + fileInfo.basename,
        // @ts-ignore - https://github.com/perry-mitchell/webdav-client/issues/329
        { headers: { Overwrite: overwrite ? 'T' : 'F' } },
      );
      return fileInfo.fileid;
    } catch (error: any) {
      if (silenceErrors) return 0;
      console.error('Failed to copy', fileInfo, error);
      if (error.response?.status === 412) {
        // Precondition failed (only if `overwrite` flag set to false)
        showError(
          t('memories', 'Could not copy {fileName}, target exists.', {
            fileName: fileInfo.filename,
          }),
        );
        return 0;
      }

      showError(
        t('memories', 'Failed to copy {fileName}.', {
          fileName: fileInfo.filename,
        }),
      );
      return 0;
    }
  };

  const calls = regularFileInfos.map((fileInfo) => () => _copy_file(fileInfo));
  yield* runInParallel(calls, 10);
  const livePhotoVideoCalls = livePhotoVideoFileInfos.map((fileInfo) => () => _copy_file(fileInfo, true));
  for await (const _ of runInParallel(livePhotoVideoCalls, 10)) {
    // ignore results for live photo video operations
  }
}

/**
 * Move all files in a given list of Ids to given destination
 *
 * @param photos list of photos to move
 * @param destination to move photos into
 * @param overwrite behaviour if the target exists. `true` overwrites, `false` fails.
 * @returns list of file ids that were moved
 */
export async function* movePhotos(photos: IPhoto[], destination: string, overwrite: boolean) {
  if (photos.length === 0) {
    return;
  }

  // Set absolute target path
  const prefixPath = `files/${utils.uid}`;
  let targetPath = prefixPath + destination;
  if (!targetPath.endsWith('/')) {
    targetPath += '/';
  }

  // Also move the stack files
  const extended = await extendWithStack(photos);
  photos = extended.photos;
  const livePhotoVideoFileIds = extended.livePhotoVideoFileIds;
  const fileIdsSet = new Set(photos.map((p) => p.fileid));

  // Get files data
  let fileInfos: IFileInfo[] = [];
  try {
    fileInfos = await getFiles(photos);
  } catch (e) {
    console.error('Failed to get file info for files to move', photos, e);
    showError(t('memories', 'Failed to move files.'));
    return;
  }

  const regularFileInfos = fileInfos.filter((f) => fileIdsSet.has(f.fileid) && !livePhotoVideoFileIds.has(f.fileid));
  const livePhotoVideoFileInfos = fileInfos.filter((f) => livePhotoVideoFileIds.has(f.fileid));

  /** Move a server file */
  const _move_file = async (fileInfo: IFileInfo, silenceErrors: boolean = false) => {
    try {
      await client.moveFile(
        fileInfo.originalFilename,
        targetPath + fileInfo.basename,
        // @ts-ignore - https://github.com/perry-mitchell/webdav-client/issues/329
        { headers: { Overwrite: overwrite ? 'T' : 'F' } },
      );
      return fileInfo.fileid;
    } catch (error: any) {
      if (silenceErrors) return 0;
      console.error('Failed to move', fileInfo, error);
      if (error.response?.status === 412) {
        // Precondition failed (only if `overwrite` flag set to false)
        showError(
          t('memories', 'Could not move {fileName}, target exists.', {
            fileName: fileInfo.filename,
          }),
        );
        return 0;
      }

      showError(
        t('memories', 'Failed to move {fileName}.', {
          fileName: fileInfo.filename,
        }),
      );
      return 0;
    }
  };

  const calls = regularFileInfos.map((fileInfo) => () => _move_file(fileInfo));
  yield* runInParallel(calls, 10);
  const livePhotoVideoCalls = livePhotoVideoFileInfos.map((fileInfo) => () => _move_file(fileInfo, true));
  for await (const _ of runInParallel(livePhotoVideoCalls, 10)) {
    // ignore results for live photo video operations
  }
}

/**
 * Move multiple files in given lists of Ids to corresponding destinations in a year/month folder structure.
 *
 * @param photos list of photos to move
 * @param destination to move photos into
 * @param overwrite behaviour if the target exists. `true` overwrites, `false` fails.
 * @returns list of file ids that were moved
 */
export async function* movePhotosByDate(photos: IPhoto[], destination: string, overwrite: boolean) {
  if (photos.length === 0) {
    return;
  }

  // Set absolute target path
  destination = `files/${utils.uid}/${destination}`;

  // Add the stacked photos
  const extended = await extendWithStack(photos);
  photos = extended.photos;
  const livePhotoVideoFileIds = extended.livePhotoVideoFileIds;
  const photosMap = new Map(photos.map((p) => [p.fileid, p]));

  let fileInfos: IFileInfo[] = [];
  try {
    fileInfos = await getFiles(photos);
  } catch (e) {
    console.error('Failed to get file info for files to move', photos, e);
    showError(t('memories', 'Failed to move files.'));
    return;
  }

  // Create the list of operations
  const operations = new Array<[string, IFileInfo]>();
  fileInfos.forEach((info) => {
    const photo = photosMap.get(info.fileid);
    if (!photo || !photo.dayid) return;

    const date = utils.dayIdToDate(photo.dayid);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');

    const path = `${destination}/${year}/${month}`;
    operations.push([path, info]);
  });

  // Cache confirmed existing directories
  const existingDirCache = new Set<string>();

  // Create all the folders first
  for (const op of operations) {
    let folderPath = op[0];
    const createPaths = new Array<string>();

    while (folderPath !== destination && folderPath.includes('/')) {
      if (existingDirCache.has(folderPath) || (await client.exists(folderPath))) {
        // No need to check further up. Go to creation.
        existingDirCache.add(folderPath);
        break;
      }

      createPaths.push(folderPath);
      folderPath = folderPath.slice(0, folderPath.lastIndexOf('/'));
    }

    // Create from top to bottom
    for (const path of createPaths.reverse()) {
      await client.createDirectory(path);
    }
  }

  /** Move server file */
  const _move_file = async (targetPath: string, fileInfo: IFileInfo, silenceErrors: boolean = false) => {
    try {
      await client.moveFile(
        fileInfo.originalFilename,
        `${targetPath}/${fileInfo.basename}`,
        // @ts-ignore - https://github.com/perry-mitchell/webdav-client/issues/329
        { headers: { Overwrite: overwrite ? 'T' : 'F' } },
      );
      return fileInfo.fileid;
    } catch (error: any) {
      if (silenceErrors) return 0;
      console.error('Failed to move', fileInfo, error);
      if (error.response?.status === 412) {
        // Precondition failed (only if `overwrite` flag set to false)
        showError(
          t('memories', 'Could not move {fileName}, target exists.', {
            fileName: fileInfo.filename,
          }),
        );
        return 0;
      }

      showError(
        t('memories', 'Failed to move {fileName}.', {
          fileName: fileInfo.filename,
        }),
      );
      return 0;
    }
  };

  // Separate live photo video operations, handle them later with errors silenced
  const nonLivePhotoOperations = operations.filter(
    ([targetPath, fileInfo]) => !livePhotoVideoFileIds.has(fileInfo.fileid),
  );
  const livePhotoVideoOperations = operations.filter(([targetPath, fileInfo]) =>
    livePhotoVideoFileIds.has(fileInfo.fileid),
  );

  const calls = nonLivePhotoOperations.map((operation) => () => _move_file(...operation));
  yield* runInParallel(calls, 10);
  const livePhotoVideoCalls = livePhotoVideoOperations.map((operation) => () => _move_file(...operation, true));
  for await (const _ of runInParallel(livePhotoVideoCalls, 10)) {
    // ignore results for live photo video operations
  }
}

/**
 * Fill the imageInfo attributes of the given photos
 *
 * @param photos list of photos to fill
 * @param query options
 * @param query.tags whether to include tags in the response
 * @param progress callback to report progress
 */
export async function fillImageInfo(photos: IPhoto[], query?: { tags?: number }, progress?: (count: number) => void) {
  // Filter out photos that are local only
  const remote = photos.filter((p) => !utils.isLocalPhoto(p));

  // Number of photos done
  let done = photos.length - remote.length;
  if (done > 0) progress?.(done);

  // Load metadata for all photos
  const calls = remote.map((p) => async () => {
    try {
      const url = API.Q(API.IMAGE_INFO(p.fileid), query ?? {});
      const res = await axios.get<IImageInfo>(url);
      p.datetaken = res.data.datetaken;
      p.imageInfo = res.data;
    } catch (error) {
      console.error('Failed to get image info', p, error);
      showError(t('memories', 'Failed to load image info: {name}', { name: p.basename ?? p.fileid }));
    } finally {
      done++;
      progress?.(done);
    }
  });

  for await (const _ of runInParallel(calls, 8)) {
    // nothing to do
  }
}
